import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { usePostUserLogout } from "../../api/userDetails";
import { DISABLE_COGNITO_LOGIN } from "../../config/constants";
import { errorView } from "../../helpers/ErrorHandler";
import { useAuthContext } from "../../hooks/useAuthContext";
import { IdleWarningModal } from "./IdleWarningModal";
import { useIdleTimer } from "./useIdleTimer";

interface IdleManagerProps {
  idleTime?: number;
  warningTime?: number;
}

export const IdleManager: React.FC<IdleManagerProps> = ({ idleTime = 480, warningTime = 10 }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { logout }: any = useAuthContext();
  const { mutate: userLogOut } = usePostUserLogout();
  const auth = useAuth();
  const navigate = useNavigate();

  const signOutRedirect = useCallback(() => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = process.env.REACT_APP_COGNITO_LOG_OUT_URI;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    auth.removeUser();
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  }, [auth]);

  const logoutUserWithCognito = useCallback(() => {
    if (DISABLE_COGNITO_LOGIN) return navigate("/auth");
    signOutRedirect();
  }, [navigate, signOutRedirect]);

  const handleUserLogout = useCallback(() => {
    logout();
    logoutUserWithCognito();
  }, [logout, logoutUserWithCognito]);

  const userLogout = useCallback(() => {
    userLogOut(undefined, {
      onSuccess: () => {
        handleUserLogout();
      },
      onError: error => {
        handleUserLogout();
        errorView("User logout fail");
      }
    });
  }, [userLogOut, handleUserLogout]);

  const handleIdle = useCallback(() => {
    setModalOpen(false);
    userLogout();
  }, [userLogout]);

  const handleWarning = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleActive = useCallback(() => {
    setModalOpen(false);
  }, []);

  const { showWarning, formattedTime, continueSession } = useIdleTimer({
    idleTime,
    warningTime,
    onIdle: handleIdle,
    onWarning: handleWarning,
    onActive: handleActive,
    modalOpen
  });

  // Sync with hook state
  useEffect(() => {
    setModalOpen(showWarning);
  }, [showWarning]);

  const handleContinue = useCallback(() => {
    setModalOpen(false);
    continueSession();
  }, [continueSession]);

  const handleLogout = useCallback(() => {
    setModalOpen(false);
    userLogout();
  }, [userLogout]);

  return (
    <IdleWarningModal
      isOpen={modalOpen}
      timeLeft={formattedTime}
      warningTime={warningTime}
      onContinue={handleContinue}
      onLogout={handleLogout}
    />
  );
};
